import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import { fetchArtist, uploadArtwork, updateArtwork, deleteArtwork, startStripeOnboarding, openStripeDashboard, deleteAccount, updateProfile, fetchArtistOrders, type Artwork, type ArtistOrder } from '../api'
import { SHIPPING_OPTIONS } from '../shipping'

// ─── Dashboard artwork card ───────────────────────────────────────────────────

function DashboardCard({
  artwork,
  onEdit,
  onDeleted,
}: {
  artwork: Artwork
  onEdit: (artwork: Artwork) => void
  onDeleted: () => void
}) {
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    if (!confirm(`Slett "${artwork.title}"? Dette kan ikke angres.`)) return
    setDeleting(true)
    try {
      await deleteArtwork(artwork.id)
      onDeleted()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Sletting feilet')
      setDeleting(false)
    }
  }

  return (
    <div className="dashboard-card">
      <Link to={`/artwork/${artwork.id}`} className="dashboard-card-img">
        <img src={artwork.imageUrl} alt={artwork.title} loading="lazy" />
        {artwork.status === 'sold' && <span className="sold-badge">Solgt</span>}
      </Link>
      <div className="dashboard-card-body">
        <p className="dashboard-card-title">{artwork.title}</p>
        <p className="dashboard-card-price">
          {artwork.priceNok.toLocaleString('nb-NO')} NOK
        </p>
        {artwork.status === 'available' ? (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button className="btn-edit" onClick={() => onEdit(artwork)}>Rediger</button>
            <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Sletter...' : 'Slett'}
            </button>
          </div>
        ) : (
          <div style={{ height: '1.75rem' }} />
        )}
      </div>
    </div>
  )
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

export function Dashboard() {
  const { artist, logout } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const stripeParam = searchParams.get('stripe')

  const [artworks, setArtworks] = useState<Artwork[]>([])
  const [loadingArtworks, setLoadingArtworks] = useState(true)
  const [editingArtwork, setEditingArtwork] = useState<Artwork | null>(null)
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  const [sales, setSales] = useState<ArtistOrder[]>([])
  const [phone, setPhone] = useState(artist?.phone ?? '')
  const [savingPhone, setSavingPhone] = useState(false)
  const [phoneSaved, setPhoneSaved] = useState(false)

  useEffect(() => {
    fetchArtistOrders().then(setSales).catch(() => undefined)
  }, [])

  useEffect(() => {
    setPhone(artist?.phone ?? '')
  }, [artist?.phone])

  const handleSavePhone = async () => {
    setSavingPhone(true)
    setPhoneSaved(false)
    try {
      await updateProfile(phone.trim() || null)
      setPhoneSaved(true)
    } finally {
      setSavingPhone(false)
    }
  }

  const [connectingStripe, setConnectingStripe] = useState(false)
  const [managingStripe, setManagingStripe] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Upload form state
  const formRef = useRef<HTMLFormElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploadCanPickup, setUploadCanPickup] = useState(false)
  const [uploadPickupLocation, setUploadPickupLocation] = useState('')

  const loadArtworks = async () => {
    if (!artist) return
    setLoadingArtworks(true)
    try {
      const profile = await fetchArtist(artist.profileSlug)
      setArtworks(profile?.artworks ?? [])
    } finally {
      setLoadingArtworks(false)
    }
  }

  useEffect(() => { loadArtworks() }, [artist?.profileSlug])

  // ?stripe=refresh means the Stripe onboarding link expired — auto-restart it
  useEffect(() => {
    if (stripeParam === 'refresh') {
      setSearchParams({}, { replace: true })
      handleConnectStripe()
    }
  }, [])

  const handleSaveEdit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!editingArtwork) return
    setEditError(null)
    const fd = new FormData(e.currentTarget)
    setSaving(true)
    try {
      await updateArtwork(editingArtwork.id, {
        title: (fd.get('title') as string).trim(),
        description: (fd.get('description') as string).trim() || undefined,
        medium: (fd.get('medium') as string).trim(),
        priceNok: parseInt(fd.get('price_nok') as string, 10),
        widthCm: (fd.get('width_cm') as string) ? parseFloat(fd.get('width_cm') as string) : null,
        heightCm: (fd.get('height_cm') as string) ? parseFloat(fd.get('height_cm') as string) : null,
        shippingOption: (fd.get('shipping_option') as string) || null,
        canPickup: fd.get('can_pickup') === '1',
        pickupLocation: (fd.get('pickup_location') as string)?.trim() || null,
      })
      setEditingArtwork(null)
      await loadArtworks()
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleManageStripe = async () => {
    setManagingStripe(true)
    try {
      const { url } = await openStripeDashboard()
      window.location.href = url
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : 'Failed to open Stripe dashboard')
      setManagingStripe(false)
    }
  }

  const handleConnectStripe = async () => {
    setConnectError(null)
    setConnectingStripe(true)
    try {
      const { url } = await startStripeOnboarding()
      window.location.href = url
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : 'Failed to start Stripe setup')
      setConnectingStripe(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (preview) URL.revokeObjectURL(preview)
    setPreview(file ? URL.createObjectURL(file) : null)
  }

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setUploadError(null)
    setUploadSuccess(false)
    const fd = new FormData(e.currentTarget)
    if (!fd.has('can_pickup')) fd.set('can_pickup', '0')
    try {
      setUploading(true)
      await uploadArtwork(fd)
      formRef.current?.reset()
      if (preview) { URL.revokeObjectURL(preview); setPreview(null) }
      setUploadCanPickup(false)
      setUploadPickupLocation('')
      setUploadSuccess(true)
      await loadArtworks()
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
  }

  const handleDeleteAccount = async () => {
    if (!confirm('Slett din konto? Dette fjerner permanent alle dine kunstverker og kan ikke angres.')) return
    setDeleteError(null)
    setDeletingAccount(true)
    try {
      await deleteAccount()
      await logout()
      navigate('/')
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account')
      setDeletingAccount(false)
    }
  }

  return (
    <main className="dashboard container">
      <title>Kontrollsenter — Originalene</title>
      <meta name="robots" content="noindex" />
      <div className="dashboard-header">
        <h1 className="dashboard-title">Kontrollsenter</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to={`/artist/${artist?.profileSlug}`} className="nav-links" style={{ fontSize: '0.875rem' }}>
            Se profil
          </Link>
          <button className="btn-logout" onClick={handleLogout}>Logg ut</button>
        </div>
      </div>

      {/* ─── Stripe connect ────────────────────────────────────────────────── */}
      <div className="stripe-section">
        {artist?.stripeConnected ? (
          <div className="stripe-connect-prompt">
            <p className="stripe-status stripe-status--ok">
              {stripeParam === 'connected'
                ? 'Stripe account connected — you can now receive payments.'
                : 'Stripe payments active'}
            </p>
            {connectError && <p className="form-error">{connectError}</p>}
            <button
              className="btn-submit"
              style={{ maxWidth: '220px' }}
              onClick={handleManageStripe}
              disabled={managingStripe}
            >
              {managingStripe ? 'Redirecting…' : 'Manage Stripe account'}
            </button>
          </div>
        ) : (
          <div className="stripe-connect-prompt">
            <p className="stripe-connect-text">
              Koble til en Stripe konto for å motta betalinger fra kjøpere.
            </p>
            {connectError && <p className="form-error">{connectError}</p>}
            <button
              className="btn-submit"
              style={{ maxWidth: '220px' }}
              onClick={handleConnectStripe}
              disabled={connectingStripe}
            >
              {connectingStripe ? 'Redirecting…' : 'Connect Stripe'}
            </button>
          </div>
        )}
      </div>

      {/* ─── Phone number ──────────────────────────────────────────────────── */}
      <div className="stripe-section">
        <p className="upload-section-title" style={{ marginBottom: '0.75rem' }}>Telefonnummer for henting</p>
        <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.75rem' }}>
          Vises til kjøper etter kjøp med henting, slik at de kan kontakte deg.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <input
            type="tel"
            className="form-input"
            placeholder="+47 000 00 000"
            value={phone}
            onChange={e => { setPhone(e.target.value); setPhoneSaved(false) }}
            style={{ maxWidth: '220px' }}
          />
          <button className="btn-submit" style={{ maxWidth: '100px' }} onClick={handleSavePhone} disabled={savingPhone}>
            {savingPhone ? 'Lagrer…' : 'Lagre'}
          </button>
          {phoneSaved && <span style={{ fontSize: '0.875rem', color: '#2a7d4f' }}>✓ Lagret</span>}
        </div>
      </div>

      {/* ─── Upload form ───────────────────────────────────────────────────── */}
      <div className="upload-section">
        <p className="upload-section-title">Legg til nytt kunstverk</p>

        {!artist?.stripeConnected && (
          <p className="upload-stripe-warning">
            For å kunne selge kunstverket må du koble til Stripe først.
          </p>
        )}

        <form ref={formRef} className="upload-form" onSubmit={handleUpload}>
          {/* Image picker */}
          <label className="upload-dropzone" htmlFor="upload-image">
            {preview ? (
              <img src={preview} alt="Preview" />
            ) : (
              <div className="upload-placeholder">
                <span>Klikk for å velge bilde</span>
                <span className="upload-hint">JPEG eller PNG, maks 10 MB</span>
              </div>
            )}
            <input
              id="upload-image"
              type="file"
              name="image"
              accept="image/jpeg,image/png"
              onChange={handleFileChange}
              required
              style={{ display: 'none' }}
            />
          </label>

          {/* Title + Medium */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="title">Tittel</label>
              <input id="title" name="title" className="form-input" required />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="medium">Medium</label>
              <input
                id="medium"
                name="medium"
                className="form-input"
                placeholder="f.eks. Oljefarge på lerret"
                required
              />
            </div>
          </div>

          {/* Price + Dimensions */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="price_nok">Pris (NOK)</label>
              <input
                id="price_nok"
                name="price_nok"
                type="number"
                min="1"
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Dimensjoner (cm, valgfritt)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  name="width_cm"
                  type="number"
                  min="0.1"
                  step="0.1"
                  className="form-input"
                  placeholder="W"
                />
                <input
                  name="height_cm"
                  type="number"
                  min="0.1"
                  step="0.1"
                  className="form-input"
                  placeholder="H"
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label" htmlFor="description">
              Beskrivelse <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(valgfritt)</span>
            </label>
            <textarea
              id="description"
              name="description"
              className="form-textarea"
              rows={3}
            />
          </div>

          {/* Shipping options */}
          <div className="form-group">
            <label className="form-label">Frakttjeneste</label>
            <select name="shipping_option" className="filter-input" style={{ width: '100%' }}>
              <option value="">Ingen frakt</option>
              <optgroup label="Posten — posten.no/sende/i-norge/norgespakke">
                {SHIPPING_OPTIONS.filter(o => o.carrier === 'Posten').map(o => (
                  <option key={o.key} value={o.key}>
                    {o.label} — {o.price} NOK ({o.description})
                  </option>
                ))}
              </optgroup>
              <optgroup label="PostNord — postnord.no/tjenester-privat/prisliste-privatkunder">
                {SHIPPING_OPTIONS.filter(o => o.carrier === 'PostNord').map(o => (
                  <option key={o.key} value={o.key}>
                    {o.label} — {o.price} NOK ({o.description})
                  </option>
                ))}
              </optgroup>
            </select>
            <label className="shipping-checkbox-label" style={{ marginTop: '0.5rem' }}>
              <input
                type="checkbox"
                name="can_pickup"
                value="1"
                checked={uploadCanPickup}
                onChange={e => setUploadCanPickup(e.target.checked)}
              />
              Kan hentes (gratis)
            </label>
            {uploadCanPickup && (
              <input
                name="pickup_location"
                className="form-input"
                placeholder="Hentested, f.eks. Oslo sentrum"
                value={uploadPickupLocation}
                onChange={e => setUploadPickupLocation(e.target.value)}
                style={{ marginTop: '0.375rem' }}
              />
            )}
          </div>

          {uploadError && <p className="form-error">{uploadError}</p>}
          {uploadSuccess && (
            <p className="upload-success">✓ Kunstverket er publisert</p>
          )}

          <button type="submit" className="btn-submit" disabled={uploading}>
            {uploading ? 'Laster opp…' : 'Publiser kunstverk'}
          </button>
        </form>
      </div>

      {/* ─── Edit form ─────────────────────────────────────────────────────── */}
      {editingArtwork && (
        <div className="upload-section">
          <p className="upload-section-title">Edit artwork</p>
          <form className="upload-form" onSubmit={handleSaveEdit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="edit-title">Tittel</label>
                <input id="edit-title" name="title" className="form-input" required defaultValue={editingArtwork.title} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="edit-medium">Medium</label>
                <input id="edit-medium" name="medium" className="form-input" required defaultValue={editingArtwork.medium} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="edit-price">Pris (NOK)</label>
                <input id="edit-price" name="price_nok" type="number" min="1" className="form-input" required defaultValue={editingArtwork.priceNok} />
              </div>
              <div className="form-group">
                <label className="form-label">Dimensjoner (cm, valgfritt)</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input name="width_cm" type="number" min="0.1" step="0.1" className="form-input" placeholder="W" defaultValue={editingArtwork.widthCm ?? ''} />
                  <input name="height_cm" type="number" min="0.1" step="0.1" className="form-input" placeholder="H" defaultValue={editingArtwork.heightCm ?? ''} />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="edit-description">
                Beskrivelse <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(valgfritt)</span>
              </label>
              <textarea id="edit-description" name="description" className="form-textarea" rows={3} defaultValue={editingArtwork.description ?? ''} />
            </div>
            <div className="form-group">
              <label className="form-label">Frakttjeneste</label>
              <select name="shipping_option" className="filter-input" style={{ width: '100%' }} defaultValue={editingArtwork.shippingOption ?? ''}>
                <option value="">Ingen frakt</option>
                <optgroup label="Posten — posten.no/sende/i-norge/norgespakke">
                  {SHIPPING_OPTIONS.filter(o => o.carrier === 'Posten').map(o => (
                    <option key={o.key} value={o.key}>
                      {o.label} — {o.price} NOK ({o.description})
                    </option>
                  ))}
                </optgroup>
                <optgroup label="PostNord — postnord.no/tjenester-privat/prisliste-privatkunder">
                  {SHIPPING_OPTIONS.filter(o => o.carrier === 'PostNord').map(o => (
                    <option key={o.key} value={o.key}>
                      {o.label} — {o.price} NOK ({o.description})
                    </option>
                  ))}
                </optgroup>
              </select>
              <label className="shipping-checkbox-label" style={{ marginTop: '0.5rem' }}>
                <input type="checkbox" name="can_pickup" value="1" defaultChecked={editingArtwork.canPickup} />
                Kan hentes (gratis)
              </label>
              {editingArtwork.canPickup && (
                <input
                  name="pickup_location"
                  className="form-input"
                  placeholder="Hentested, f.eks. Oslo sentrum"
                  defaultValue={editingArtwork.pickupLocation ?? ''}
                  style={{ marginTop: '0.375rem' }}
                />
              )}
            </div>
            {editError && <p className="form-error">{editError}</p>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button type="submit" className="btn-submit" disabled={saving}>
                {saving ? 'Lagrer…' : 'Lagre endringer'}
              </button>
              <button type="button" className="btn-logout" onClick={() => { setEditingArtwork(null); setEditError(null) }}>
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── Listings ──────────────────────────────────────────────────────── */}
      <p className="listings-title">Dine oppføringer</p>

      {loadingArtworks ? (
        <p className="state-loading">Laster inn…</p>
      ) : artworks.length === 0 ? (
        <p className="state-empty" style={{ textAlign: 'left', padding: '1rem 0' }}>
          Ingen kunstverk enda — last opp ditt første stykke over.
        </p>
      ) : (
        <div className="artwork-grid">
          {artworks.map(a => (
            <DashboardCard key={a.id} artwork={a} onEdit={setEditingArtwork} onDeleted={loadArtworks} />
          ))}
        </div>
      )}
      {/* ─── Sales ─────────────────────────────────────────────────────────── */}
      {sales.length > 0 && (
        <>
          <p className="listings-title">Dine salg</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2.5rem' }}>
            {sales.map(s => (
              <div key={s.id} className="upload-section" style={{ padding: '1rem 1.25rem' }}>
                <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{s.artworkTitle}</p>
                <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                  {s.priceNok.toLocaleString('nb-NO')} NOK
                  {s.shippingNok > 0 && ` + ${s.shippingNok.toLocaleString('nb-NO')} NOK frakt`}
                  {' · '}
                  {s.shippingMethod === 'pickup' ? `Hentes${s.buyerAddress ? ` — ${s.buyerAddress}` : ''}` : `Sendes til ${s.buyerAddress ?? '…'}`}
                </p>
                <p style={{ fontSize: '0.875rem' }}>
                  <strong>{s.buyerName}</strong>
                  {s.buyerPhone && (
                    <> · <a href={`tel:${s.buyerPhone}`} style={{ color: '#111' }}>{s.buyerPhone}</a></>
                  )}
                </p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ─── Danger zone ───────────────────────────────────────────────────── */}
      <div className="danger-zone">
        <p className="danger-zone-title">Danger zone</p>
        {deleteError && <p className="form-error" style={{ marginBottom: '0.75rem' }}>{deleteError}</p>}
        <button className="btn-danger" onClick={handleDeleteAccount} disabled={deletingAccount}>
          {deletingAccount ? 'Deleting…' : 'Delete account'}
        </button>
      </div>
    </main>
  )
}
